import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactMessage, setContactMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setContactStatus('success');
      setContactMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('name', formData.get('name') as string);
      params.append('email', formData.get('email') as string);
      params.append('message', formData.get('message') as string);
      const res = await fetch('https://readdy.ai/api/form/d9sqg1llbrt1qcgaj8ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setContactMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        setContactStatus('error');
        setContactMessage(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。');
      }
    } catch {
      setContactStatus('error');
      setContactMessage('网络错误，请稍后再试。');
    }
  };

  const testimonials = [
    {
      name: '小北',
      text: '在这个平台上读到了很多感同身受的故事。那些关于暗恋朋友姐姐的文字，让我想起了自己十五岁时的心跳。很温暖，很治愈。',
      role: '忠实读者',
    },
    {
      name: '清风',
      text: '心理分析的文章特别棒，让我第一次从专业角度理解了自己当年的那些"奇怪"行为。原来我不是一个人，原来这些都是正常的。',
      role: '心理学爱好者',
    },
    {
      name: '阿远',
      text: '感谢这个平台让我有勇气面对自己的过去。读完那篇关于告别的文章后，我终于放下了藏在心里多年的那份情感。真的是一个很用心的平台。',
      role: '情感读者',
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 朋友的姐姐',
            description: '了解朋友的姐姐品牌故事、用户口碑与联系方式。专注于青春期情感心理、成长记忆与青春文学的内容平台。',
            url: typeof window !== 'undefined' ? window.location.href : '',
            isPartOf: {
              '@type': 'WebSite',
              name: '朋友的姐姐',
              url: typeof window !== 'undefined' ? window.location.origin : '',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '朋友的姐姐',
            url: typeof window !== 'undefined' ? window.location.origin : '',
            description: '专注于青春期情感心理、成长记忆与青春文学的内容平台。通过真实故事、心理分析和文学作品，帮助读者理解和珍视青春情感经历。',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />

      <div className="pt-20 min-h-screen bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Brand Introduction */}
          <section className="mt-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">关于我们</h1>
            <div className="mt-6 p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4">品牌故事</h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  「朋友的姐姐」是一个专注于青春期情感心理与成长记忆的内容平台。我们相信，每个人心中都住着一个"朋友的姐姐"——那个让你第一次心跳加速、第一次学会偷偷喜欢、第一次理解什么是"不可得的美好"的人。
                </p>
                <p>
                  我们的团队由心理学研究者、情感作家和内容创作者组成。我们致力于通过真实的情感故事、专业的心理分析和动人的青春文学，帮助每一位读者理解自己那段青涩的成长经历，接纳那些藏在心底的温柔秘密。
                </p>
                <p>
                  在这里，你会发现——你不是一个人在经历这些。那些年少时的悸动、暗恋、仰慕和告别，都是成长的必经之路。而「朋友的姐姐」，正是青春赠予我们的一场温柔的预习课。
                </p>
              </div>
            </div>
          </section>

          {/* Our Mission */}
          <section className="mt-10">
            <h2 className="text-xl font-semibold text-foreground-900 mb-6">我们的使命</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: 'ri-heart-pulse-line', title: '情感共鸣', desc: '通过真实故事和深度内容，让每一位读者找到属于自己的情感共鸣。' },
                { icon: 'ri-psychotherapy-line', title: '心理科普', desc: '用专业的心理学知识，帮助读者理解自己的情感经历和成长轨迹。' },
                { icon: 'ri-book-3-line', title: '文学表达', desc: '用优美的文字和动人的故事，为青春情感提供最温柔的文学注解。' },
              ].map((item, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-accent-700`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="mt-14">
            <h2 className="text-xl font-semibold text-foreground-900 mb-6">读者口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm text-yellow-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.text}</p>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="mt-14 mb-10">
            <h2 className="text-xl font-semibold text-foreground-900 mb-6">联系我们</h2>
            <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70">
              <p className="text-sm text-foreground-600 mb-6">
                如果你有任何想法、故事想要分享，或者有任何问题想要咨询，欢迎通过以下表单与我们联系。我们期待听到你的声音。
              </p>
              <form onSubmit={handleContactSubmit} data-readdy-form="" className="space-y-5 max-w-lg">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-900 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-900 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-900 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                  style={{ position: 'absolute', left: '-9999px' }}
                />
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>
                {contactStatus !== 'idle' && (
                  <p className={`text-sm ${contactStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                    {contactMessage}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}